/**
 * TwoFactorDisableDialog — confirm + disable 2FA after verification.
 * Path A 2FA Phase 2 (2026-05-26).
 *
 * Backend accepts either a 6-digit TOTP or a 10-char recovery code.
 * UI lets users paste either; backend disambiguates.
 */

import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { twoFactorApi } from '../../api/twofa';
import { ApiError } from '../../api/client';
import { isStepUpCancelled } from '../../api/stepup';
import { translateApiError } from '../../i18n/translateApiError';

interface Props {
  open: boolean;
  onClose: () => void;
  onDisabled: () => void;
}

const TwoFactorDisableDialog: React.FC<Props> = ({
  open,
  onClose,
  onDisabled,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    onClose();
    setTimeout(() => {
      setCode('');
      setError(null);
    }, 200);
  };

  const handleDisable = async () => {
    if (!code) {
      setError(t('common:security.enter6DigitOrRecovery'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await twoFactorApi.disable(code.trim());
      onDisabled();
      handleClose();
    } catch (e) {
      // The user declined the password prompt. Not a failure — rendering the
      // step-up demand here would tell them to do the thing they just refused
      // (owner report 2026-08-01, where it surfaced as the raw code).
      if (isStepUpCancelled(e)) return;
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>{t('common:twoFactor.disable.title')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('common:twoFactor.disable.warning')}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('common:twoFactor.disable.body')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label={t('common:security.codeLabel')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code && !loading) {
              handleDisable();
            }
          }}
          placeholder={t('common:security.codePlaceholder')}
          inputProps={{
            autoComplete: 'one-time-code',
            // 10 = a recovery code, the longer of the two credentials this accepts
            // (a TOTP code is 6). It was 16, which is neither, and an arbitrary cap
            // on a credential field is how the hosted sign-in ended up letting two
            // pasted recovery codes in (owner report 2026-08-03). No inputMode here
            // on purpose: a recovery code is alphanumeric.
            maxLength: 10,
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDisable}
          disabled={loading || !code}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t('common:twoFactor.disable.disabling') : t('common:twoFactor.disable.button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorDisableDialog;
